"use client";

/**
 * Loading state for team chat. It mirrors the real layout — channel rail,
 * header, message rows, composer — so the content lands where the skeleton
 * already sits instead of replacing a centred spinner with a full screen.
 *
 * Row shapes come from a fixed table rather than random widths: random means
 * a different skeleton on every render, and re-rendering during load makes it
 * twitch.
 */

const BAR = "rounded bg-zinc-200/80 dark:bg-zinc-800";

// [avatar?, name width, body line widths] — uneven on purpose so it reads as
// conversation rather than a stack of identical bars.
const ROWS: Array<{ grouped?: boolean; name: number; lines: number[] }> = [
  { name: 84, lines: [92, 61] },
  { grouped: true, name: 0, lines: [47] },
  { name: 68, lines: [74] },
  { name: 96, lines: [88, 70, 35] },
  { grouped: true, name: 0, lines: [56] },
  { name: 76, lines: [64] },
  { name: 88, lines: [80, 44] },
];

const CHANNELS = [72, 58, 84, 64, 78, 52];

export function TeamChatSkeleton() {
  return (
    <div
      className="absolute inset-0 z-[60] flex bg-white dark:bg-zinc-950"
      role="status"
      aria-label="Loading team chat"
    >
      {/* Channel rail — hidden on phones, where the real one is a drawer. */}
      <div className="hidden md:flex w-56 flex-col border-r border-zinc-200 dark:border-zinc-800 p-3 gap-4">
        <div className="tc-shimmer relative overflow-hidden flex items-center gap-2">
          <div className={`${BAR} h-7 w-7 rounded-lg`} />
          <div className={`${BAR} h-3 w-24`} />
        </div>

        <div className="flex flex-col gap-3">
          <div className="tc-shimmer relative overflow-hidden">
            <div className={`${BAR} h-2 w-16`} />
          </div>
          <div className="tc-shimmer relative overflow-hidden flex flex-col gap-2.5">
            {CHANNELS.map((w, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`${BAR} h-3 w-3`} />
                <div className={`${BAR} h-2.5`} style={{ width: w }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="tc-shimmer relative overflow-hidden flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-4 h-14">
          <div className={`${BAR} h-4 w-4`} />
          <div className={`${BAR} h-3 w-32`} />
          <div className={`${BAR} h-2.5 w-20 ml-2`} />
          <div className={`${BAR} h-7 w-7 rounded-full ml-auto`} />
        </div>

        <div className="tc-shimmer relative overflow-hidden flex-1 flex flex-col justify-end gap-4 px-4 py-4">
          {ROWS.map((row, i) => (
            <div key={i} className="flex gap-3">
              {row.grouped ? (
                <div className="w-9 flex-shrink-0" />
              ) : (
                <div className={`${BAR} h-9 w-9 rounded-full flex-shrink-0`} />
              )}
              <div className="flex-1 min-w-0 flex flex-col gap-2 pt-0.5">
                {row.name > 0 && (
                  <div className="flex items-center gap-2">
                    <div className={`${BAR} h-2.5`} style={{ width: row.name }} />
                    <div className={`${BAR} h-2 w-10`} />
                  </div>
                )}
                {row.lines.map((w, j) => (
                  <div key={j} className={`${BAR} h-2.5 max-w-full`} style={{ width: `${w}%` }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 pb-4">
          <div className="tc-shimmer relative overflow-hidden flex items-center gap-2 rounded-xl border border-zinc-200 dark:border-zinc-800 px-3 h-12">
            <div className={`${BAR} h-4 w-4`} />
            <div className={`${BAR} h-2.5 w-40`} />
            <div className={`${BAR} h-5 w-5 rounded-md ml-auto`} />
            <div className={`${BAR} h-7 w-7 rounded-lg`} />
          </div>
        </div>
      </div>

      <span className="sr-only">Loading team chat…</span>
    </div>
  );
}

/** The same message rows on their own, for when only the channel is switching. */
export function MessageListSkeleton() {
  return (
    <div
      className="tc-shimmer relative overflow-hidden flex-1 flex flex-col justify-end gap-4 px-4 py-4"
      role="status"
      aria-label="Loading messages"
    >
      {ROWS.map((row, i) => (
        <div key={i} className="flex gap-3">
          {row.grouped ? (
            <div className="w-9 flex-shrink-0" />
          ) : (
            <div className={`${BAR} h-9 w-9 rounded-full flex-shrink-0`} />
          )}
          <div className="flex-1 min-w-0 flex flex-col gap-2 pt-0.5">
            {row.name > 0 && (
              <div className="flex items-center gap-2">
                <div className={`${BAR} h-2.5`} style={{ width: row.name }} />
                <div className={`${BAR} h-2 w-10`} />
              </div>
            )}
            {row.lines.map((w, j) => (
              <div key={j} className={`${BAR} h-2.5 max-w-full`} style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      ))}
      <span className="sr-only">Loading messages…</span>
    </div>
  );
}
